import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Home as HomeIcon,
  Event as ActivityIcon,
  AccessTime as PetsIcon,
  Call as CallsIcon,
  Storage as DataIcon,
  Map as MapIcon, // Import the Map icon
  ViewTimeline as ViewTimelineIcon,
  Email as EmailIcon,
  CheckBox as TasksIcon,
  Anchor as AnchorIcon,
  DirectionsBoat as TerminalIcon,
  Navigation as PilotTrackerIcon,
  TravelExplore as UpdateIcon,
  Analytics as AnalyticsIcon,
  CalendarMonth as StaffDutyCalendarIcon,
} from "@mui/icons-material";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Box,
  IconButton,
  Tooltip,
  Badge,
} from "@mui/material";
import React, { useState, useEffect, useContext, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

import AWSCognitoContext from "../../contexts/aws-cognito-context";
import {
  isInternalFeatureEnabled,
  userCanAccessInternalFeature,
} from "../../utils/featureFlags";

import type { RootState } from "../../store/reducers/reducer";
import type { Task } from "../../utils/types/tasks";

const drawerWidth = 200;
const collapsedWidth = 60;

const Sidebar: React.FC<{ onWidthChange: (width: number) => void }> = ({
  onWidthChange,
}) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useContext(AWSCognitoContext);
  const { tasks: taskList } = useSelector((state: RootState) => state.tasks);
  const personId = auth?.user?.person_id;
  const canUseInternalFeatures = userCanAccessInternalFeature(auth?.user);

  // Calculate task badge count and color
  const taskBadge = useMemo(() => {
    if (!personId || !taskList || taskList.length === 0) return null;

    const now = new Date();
    const next24Hours = new Date(now.getTime() + 24 * 60 * 60 * 1000);

    const myActiveTasks = taskList.filter(
      (task: Task) =>
        task.agentId === personId &&
        (task.status === "todo" || task.status === "blocked" || task.status === "in-progress")
    );

    if (myActiveTasks.length === 0) return null;

    // Check for past due tasks (red badge)
    const hasPastDue = myActiveTasks.some(
      (task: Task) => task.dueDate && new Date(task.dueDate) < now
    );

    if (hasPastDue) {
      return { count: myActiveTasks.length, color: "error" as const };
    }

    // Check for tasks due in next 24 hours (orange badge)
    const hasDueSoon = myActiveTasks.some(
      (task: Task) =>
        task.dueDate && new Date(task.dueDate) >= now && new Date(task.dueDate) <= next24Hours
    );

    if (hasDueSoon) {
      return { count: myActiveTasks.length, color: "warning" as const };
    }

    // Default blue badge
    return { count: myActiveTasks.length, color: "primary" as const };
  }, [taskList, personId]);

  // Notify parent about sidebar width changes
  useEffect(() => {
    onWidthChange(open ? drawerWidth : collapsedWidth);
  }, [open, onWidthChange]);

  const toggleDrawer = () => {
    setOpen((prev) => !prev);
  };

  const menuItems = [
    { icon: <HomeIcon />, label: "Clients", path: "/voyages" },
    ...(isInternalFeatureEnabled("fleetMap") && canUseInternalFeatures
      ? [{ icon: <UpdateIcon />, label: "Update", path: "/update" }]
      : []),
    { icon: <TerminalIcon />, label: "Terminals", path: "/terminals" },
    { icon: <TasksIcon />, label: "Tasks", path: "/tasks" },
    { icon: <EmailIcon />, label: "Emails", path: "/emails" },
    { icon: <ActivityIcon />, label: "Activity", path: "/activities" },
    { icon: <ViewTimelineIcon />, label: "Timeline", path: "/timeline" },
    { icon: <PetsIcon />, label: "PETS", path: "/pets" },
    { icon: <CallsIcon />, label: "Calls", path: "/calls" },
    { icon: <MapIcon />, label: "Map", path: "/maps" },
    { icon: <AnchorIcon />, label: "Husbandry", path: "/husbandry" },
    { icon: <PilotTrackerIcon />, label: "Pilot Tracker", path: "/pilot-tracker" },
    { icon: <DataIcon />, label: "Data", path: "/data/people" },
    {
      icon: <StaffDutyCalendarIcon />,
      label: "Staff Duty Calendar",
      path: "/staff-duty-calendar",
    },
    { icon: <AnalyticsIcon />, label: "Stats", path: "/stats" },
    ...(isInternalFeatureEnabled("charterAnalytics") && canUseInternalFeatures
      ? [{ icon: <AnalyticsIcon />, label: "Analytics", path: "/analytics" }]
      : []),
  ];

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: open ? drawerWidth : collapsedWidth,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: open ? drawerWidth : collapsedWidth,
          transition: "width 0.3s",
          boxSizing: "border-box",
          overflowX: "hidden",
        },
      }}
    >
      <Toolbar>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          width="100%"
        >
          <IconButton onClick={toggleDrawer}>
            {open ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>
        </Box>
      </Toolbar>
      <List>
        {menuItems.map((item) => {
          const isTasksItem = item.label === "Tasks";
          const showBadge = isTasksItem && taskBadge;

          return (
            <ListItem
              key={item.path}
              disablePadding
            >
              <ListItemButton
                onClick={() => navigate(item.path)}
                selected={location.pathname === item.path}
              >
                <ListItemIcon>
                  <Tooltip title={item.label} placement="right" arrow>
                    {showBadge ? (
                      <Badge
                        badgeContent={taskBadge.count}
                        color={taskBadge.color}
                        max={99}
                      >
                        {item.icon}
                      </Badge>
                    ) : (
                      item.icon
                    )}
                  </Tooltip>
                </ListItemIcon>
                {open && <ListItemText primary={item.label} />}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Drawer>
  );
};

export default Sidebar;
